import { IChartScope, PolicyType } from "@/api";
import type {
    IExtractedFieldResponse,
    IExtractedPropertyValue,
} from "@/api/documents/documents.types";
import { alpha, Box } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import React from "react";
import { Table } from "../../../../../components/shared/table/Table";
import { FieldCellData, FieldValueCell } from "./FieldValueCell";

interface FieldRow {
    id: string;
    hierarchy: string[];
    fieldName: string;
    propertyName?: string;
    isGroup?: boolean; // Flag to indicate if this is a parent/group row
    [key: string]:
        | string
        | string[]
        | boolean
        | FieldCellData
        | null
        | undefined; // Dynamic scope columns
}

interface FieldComparisonDataGridProps {
    scopes: IChartScope[];
    policyType: PolicyType;
}

const buildScopeColumnsV2 = (
    scopes: IChartScope[],
    fieldDataByScope: Record<number, IExtractedFieldResponse> | null,
    propertyName: string | null
): Record<string, FieldCellData | null> => {
    const columns: Record<string, FieldCellData | null> = {};

    scopes.forEach((scope) => {
        const field = fieldDataByScope?.[scope.id];
        // V2 fields have items[0].properties for scalar fields
        const properties = field?.items?.[0]?.properties;
        const property = properties?.find(
            (p: IExtractedPropertyValue) => p.property_name === propertyName
        );

        columns[`scope_${scope.id}`] = property
            ? {
                  value: property.value || null,
                  pageNum: property.page_number || null,
                  scopeId: scope.id,
              }
            : null;
    });

    return columns;
};

/**
 * Get aligned scalar field names across all scopes for V2 data.
 * Only includes scalar fields (is_array=false).
 */
const alignFieldsAcrossScopesV2 = (
    scopes: IChartScope[],
    policyType: string
): string[] => {
    const fieldNamesSet = new Set<string>();

    scopes.forEach((scope) => {
        const fieldGroup = scope.extracted_fields_v2?.find(
            (fg) => fg.policy_type === policyType
        );

        if (fieldGroup) {
            // Only use scalar_fields (is_array=false is already filtered)
            fieldGroup.scalar_fields.forEach((field) => {
                // Only include fields that have at least one property with a value
                const hasValue = field.items.some((item) =>
                    item.properties.some((prop) => prop.value)
                );
                if (hasValue) {
                    fieldNamesSet.add(field.field_name);
                }
            });
        }
    });

    return Array.from(fieldNamesSet);
};

/**
 * Build data grid rows from V2 extracted fields structure.
 * Uses only scalar_fields (array fields are excluded).
 */
const buildDataGridRowsV2 = (
    scopes: IChartScope[],
    policyType: string
): FieldRow[] => {
    const rows: FieldRow[] = [];
    const alignedFieldNames = alignFieldsAcrossScopesV2(scopes, policyType);

    // Sort field names to ensure "Policy Information" appears first
    const sortedFieldNames = alignedFieldNames.sort((a, b) => {
        if (a === "Policy Information") return -1;
        if (b === "Policy Information") return 1;
        return 0;
    });

    sortedFieldNames.forEach((fieldName) => {
        // Collect field data from all scopes
        const fieldDataByScope: Record<number, IExtractedFieldResponse> = {};

        scopes.forEach((scope) => {
            const fieldGroup = scope.extracted_fields_v2?.find(
                (fg) => fg.policy_type === policyType
            );

            if (fieldGroup) {
                const field = fieldGroup.scalar_fields.find(
                    (f) => f.field_name === fieldName
                );
                if (field) {
                    fieldDataByScope[scope.id] = field;
                }
            }
        });

        // Get the first available field to check property count
        const firstField = Object.values(fieldDataByScope)[0];
        if (!firstField || !firstField.items?.[0]?.properties) return;

        const properties = firstField.items[0].properties;
        const propertyCount = properties.length;

        if (propertyCount > 1) {
            // Multi-property: Create parent + child rows

            // Collect all unique property names across all scopes for this field
            const propertyNamesSet = new Set<string>();
            Object.values(fieldDataByScope).forEach((field) => {
                field.items[0]?.properties.forEach((prop) => {
                    propertyNamesSet.add(prop.property_name);
                });
            });

            // Filter to only include properties that have a value in at least one scope
            const propertiesToShow = Array.from(propertyNamesSet).filter(
                (propertyName) => {
                    return Object.values(fieldDataByScope).some((field) => {
                        const prop = field.items[0]?.properties.find(
                            (p) => p.property_name === propertyName
                        );
                        return prop && prop.value;
                    });
                }
            );

            // Only create rows if there are properties to show
            if (propertiesToShow.length === 0) return;

            // Show flattened row if only one property has value
            if (propertiesToShow.length === 1) {
                const prop = propertiesToShow[0];

                rows.push({
                    id: `field-${firstField.field_definition_id}`,
                    hierarchy: [`${fieldName} ${prop}`],
                    fieldName: `${fieldName} ${prop}`,
                    propertyName: prop,
                    ...buildScopeColumnsV2(scopes, fieldDataByScope, prop),
                });
            } else {
                // Show parent + child rows
                // Parent row
                rows.push({
                    id: `field-${firstField.field_definition_id}`,
                    hierarchy: [fieldName],
                    fieldName: fieldName,
                    isGroup: true, // Mark as group row
                    // Parent rows show "-" in all scope columns
                    ...Object.fromEntries(
                        scopes.map((scope) => [`scope_${scope.id}`, null])
                    ),
                });

                // Child rows for each property that has a value in at least one scope
                // Sort by property_sort_order
                const sortedProperties = propertiesToShow.sort((a, b) => {
                    const propA = firstField.items[0]?.properties.find(
                        (p) => p.property_name === a
                    );
                    const propB = firstField.items[0]?.properties.find(
                        (p) => p.property_name === b
                    );
                    return (
                        (propA?.property_sort_order ?? 0) -
                        (propB?.property_sort_order ?? 0)
                    );
                });

                sortedProperties.forEach((propertyName, idx) => {
                    rows.push({
                        id: `field-${firstField.field_definition_id}-prop-${idx}`,
                        hierarchy: [fieldName, propertyName],
                        fieldName: propertyName,
                        propertyName: propertyName,
                        ...buildScopeColumnsV2(
                            scopes,
                            fieldDataByScope,
                            propertyName
                        ),
                    });
                });
            }
        } else if (propertyCount === 1) {
            // Single-property: Create flat row with combined name
            const prop = properties[0];

            // Check if this property has a value in at least one scope
            const hasValueInAnyScope = Object.values(fieldDataByScope).some(
                (field) => {
                    const property = field.items[0]?.properties.find(
                        (p) => p.property_name === prop.property_name
                    );
                    return property && property.value;
                }
            );

            // Only create the row if the property has a value in at least one scope
            if (!hasValueInAnyScope) return;

            rows.push({
                id: `field-${firstField.field_definition_id}`,
                hierarchy: [`${fieldName} ${prop.property_name}`],
                fieldName: `${fieldName} ${prop.property_name}`,
                propertyName: prop.property_name,
                ...buildScopeColumnsV2(
                    scopes,
                    fieldDataByScope,
                    prop.property_name
                ),
            });
        }
    });

    return rows;
};

export const FieldComparisonDataGrid: React.FC<FieldComparisonDataGridProps> =
    React.memo(({ scopes, policyType }) => {
        const rows = React.useMemo(
            () => buildDataGridRowsV2(scopes, policyType),
            [scopes, policyType]
        );

        // Memoize columns to prevent recreation on every render
        const columns: GridColDef[] = React.useMemo(
            () => [
                ...scopes.map(
                    (scope, index) =>
                        ({
                            field: `scope_${scope.id}`,
                            headerName: scope.document_title,
                            maxWidth: 400,
                            minWidth: 400,
                            cellClassName:
                                index % 2 === 0
                                    ? "scope-column-even"
                                    : "scope-column-odd",
                            headerClassName: "scope-header",
                            renderCell: (params: GridRenderCellParams) => {
                                // Don't render values for group/parent rows
                                if (params.row.isGroup) {
                                    return null;
                                }
                                return (
                                    <FieldValueCell
                                        data={
                                            params.value as FieldCellData | null
                                        }
                                    />
                                );
                            },
                            sortable: false,
                            filterable: false,
                            disableColumnMenu: true,
                            disableReorder: true,
                        }) as GridColDef
                ),
            ],
            [scopes]
        );

        const autoSizeOptions = {
            columns: [
                "__tree_data_group__",
                ...columns.map((col) => col.field),
            ],
            includeHeaders: true,
            includeOutliers: true,
            expand: true,
        };

        return (
            <Box sx={{ width: "100%", p: 2 }}>
                <Table
                    rows={rows}
                    columns={columns}
                    treeData
                    getTreeDataPath={(row) => row.hierarchy}
                    groupingColDef={{
                        headerName: "Field",
                        width: 400,
                        hideable: false,
                        hideDescendantCount: true,
                        sortable: false,
                    }}
                    defaultGroupingExpansionDepth={-1}
                    disableChildrenFiltering={false}
                    autosizeOptions={autoSizeOptions}
                    autoSize
                    hideFooter
                    disableRowSelectionOnClick
                    sx={{
                        bgcolor: "background.paper",
                        border: 0,

                        "& .MuiDataGrid-columnHeader": {
                            bgcolor: "background.paper",
                            borderRadius: "0px !important",
                            color: "primary.main",
                            outline: "none !important",
                        },

                        // Enhanced header styling for scope columns
                        "& .MuiDataGrid-columnHeader.scope-header": {
                            bgcolor: "grey.50",
                            fontWeight: 600,
                            borderLeft: "1px solid",
                            borderColor: "divider",
                        },

                        "& .MuiDataGrid-filler": {
                            bgcolor: "background.paper",
                        },
                        "& .MuiDataGrid-topContainer": {
                            bgcolor: "background.paper",
                        },

                        ".MuiDataGrid-cell:focus": {
                            outline: "none",
                        },

                        "& .MuiDataGrid-columnSeparator": {
                            display: "none",
                        },

                        "& .MuiDataGrid-cell": {
                            py: "8px",
                        },

                        // Alternating column backgrounds for scope columns
                        "& .MuiDataGrid-cell.scope-column-even, & .MuiDataGrid-cell.scope-column-odd":
                            {
                                borderLeft: "1px solid",
                                borderColor: "divider",
                            },

                        "& .MuiDataGrid-cell.scope-column-odd": {
                            bgcolor: (theme) =>
                                theme.palette.mode === "light"
                                    ? alpha(theme.palette.primary.main, 0.03)
                                    : alpha(theme.palette.primary.main, 0.05),
                        },
                    }}
                />
            </Box>
        );
    });
